import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import axios, { AxiosError } from 'axios'
import type {
  Coupon,
  CouponPayload,
  TransformedCoupon,
} from '@/interface/Coupon'
import { useUserStore } from '@/stores/user'
import { URLs } from '@/constants'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { convertServerTimeToLocalTime, getCouponsApi } from '@/utils'
import { fetchCoupons } from '@/services/coupons'
import { useProductSettingsStore } from '@/stores/productSettings'
import type { FullCoupon } from '@/interface/Coupon'

export const useCounterStore = defineStore('counter', () => {
  const count = ref(0)
  const doubleCount = computed(() => count.value * 2)
  const coupons = ref<FullCoupon[]>([])

  const accountSettings = useAccountSettingsStore()
  const productSettings = useProductSettingsStore()

  function increment() {
    count.value++
  }

  function getEndpointURL(userType: string, couponId: string): string {
    if (couponId != null && couponId !== '') {
      return `${URLs.COUPONS_API_URL}/user/coupons`
    } else {
      return `${URLs.COUPONS_API_URL}/widget/${couponId}`
    }
  }

  async function fetchAndSetCoupons() {
    const getCouponsParams = {
      endpoint: getCouponsApi(),
      accountId: accountSettings.accountId,
      couponId: productSettings.couponId,
      isClientUser: Boolean(accountSettings.isClientUser),
      token: accountSettings.sessionToken,
    }

    // Convert the startTime and endTime to local date-time input format
    try {
      const raw = await fetchCoupons(getCouponsParams)
      coupons.value = raw.map(c => ({
        ...c,
        creationDate: c.creationDate
          ? convertServerTimeToLocalTime(c.creationDate)
          : '',
        startTime: c.startTime ? convertServerTimeToLocalTime(c.startTime) : '',
        endTime: c.endTime ? convertServerTimeToLocalTime(c.endTime) : '',
      }))
      console.log('COUPONS +++++++ ', coupons.value)
    } catch (err) {
      console.error('Error fetching coupons:', err)
    }
  }

  async function fetchChildCoupon(parentCouponId: string) {
    const userStore = useUserStore()
    try {
      const response = await axios.get(
        getEndpointURL(
          userStore?.userType || '',
          userStore?.productSetting?.couponId || '',
        ),
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userStore?.cognitoToken}`,
          },
          params: { parentCouponId },
        },
      )
      console.log('response', response)

      const data = response.data.body?.coupons || response.data.coupons || []

      const currentDate = new Date()

      coupons.value = data.map((coupon: Coupon, index: number) => {
        const endDate = new Date(coupon.endTime)
        const currentDate = new Date()

        let status = coupon.status

        if (status === null || status === undefined) {
          status = endDate < currentDate ? 'Expired' : 'Active'
        } else if (endDate < currentDate) {
          status = 'Expired'
        }

        return {
          index: index + 1,
          code: coupon.couponId,
          title: coupon?.design?.title || 'No Title',
          startTime: coupon.startTime,
          endTime: coupon.endTime,
          stats: status,
          pauseReason: coupon.pauseReason,
          status: status,
          recurring: coupon.isRecurring,
        }
      })
    } catch (error) {
      console.error('Error fetching coupons:', error)
      const userStore = useUserStore()
      // userStore.$reset();
      // localStorage.clear();
      // userStore.clearUser()
      // window.location.href = "/";
    }
  }

  async function createCoupon(newCoupon: CouponPayload) {
    try {
      const endpoint = URLs.COUPONS_API_URL
      const path = `admin/coupons`
      const response = await axios.post(
        `${endpoint}/${path}`,
        {
          ...newCoupon,
          accountId: accountSettings.accountId,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accountSettings?.sessionToken}`,
          },
          params: {
            accountId: accountSettings.accountId,
            isClient: accountSettings.isClientUser,
          },
        },
      )
      console.log('Coupon created:', response.data)
    } catch (error) {
      console.error('Error creating coupon:', error)
    }
  }

  async function getCouponDetails(couponId: string) {
    try {
      const endpoint = URLs.COUPONS_API_URL
      const path = `admin/coupons`
      const response = await axios.get(`${endpoint}/${path}`, {
        params: { couponId },
        headers: {
          'Content-Type': 'application/json',
        },
      })
      const fetchedCoupon = response.data
      console.log('Fetched coupon details:', fetchedCoupon)
      return fetchedCoupon
    } catch (error) {
      console.error('Error fetching coupon details:', error)
      //     const userStore = useUserStore();
      //     userStore.$reset();
      //     localStorage.clear();
      //     userStore.clearUser()
      // window.location.href = "/";
      // return null;
    }
  }

  async function updateCoupon(newCoupon: FullCoupon & { couponId: string }) {
    console.log('Updating coupon with data:', newCoupon)
    const endpoint = URLs.COUPONS_API_URL
    const path = `admin/coupons`
    try {
      const response = await axios.put(`${endpoint}/${path}`, newCoupon, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accountSettings?.sessionToken}`,
        },
        params: {
          isClient: accountSettings.isClientUser,
        },
      })
      console.log('Coupon created:', response.data)
    } catch (error) {
      console.error('Error creating coupon:', error)
    }
  }

  async function deleteCoupon(couponId: string) {
    try {
      const endpoint = URLs.COUPONS_API_URL
      const path = `admin/coupons`
      const response = await axios.delete(`${endpoint}/${path}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accountSettings?.sessionToken}`,
        },
        params: {
          couponId: couponId,
          isClient: accountSettings.isClientUser,
        },
      })
      console.log('Coupon deleted:', response.data)
      return true
      // await fetchCoupons();
    } catch (error) {
      console.error('Error deleting coupon:', error)
      if (error instanceof AxiosError) {
        alert(error?.response?.data?.message)
      } else if (error instanceof Error) {
        alert(error.message)
      } else {
        alert('An unexpected error occurred while deleting the coupon.')
      }
    }
  }

  async function pauseCoupon(
    couponId: string,
    isPaused: boolean,
    pauseReason = '',
  ) {
    try {
      const userStore = useUserStore()
      const funcUser = () => {
        if (
          userStore?.userType == 'company_admin' ||
          userStore?.userType == 'company_suadmin' ||
          userStore?.userType == 'company_owner' ||
          userStore?.userType == 'company_user'
        ) {
          return 'admin'
        } else if (
          userStore?.userType == 'client_admin' ||
          userStore?.userType == 'client_manager' ||
          userStore?.userType == 'client_user'
        ) {
          return 'user'
        }
      }

      const response = await axios.patch(
        `${getEndpointURL(
          userStore?.userType || '',
          userStore?.productSetting?.couponId || '',
        )}/pause`,
        {
          couponId: couponId,
          isPaused: isPaused,
          pauseReason: isPaused ? pauseReason : undefined,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userStore?.cognitoToken}`,
          },
        },
      )
      console.log('Coupon pause status updated:', response.data)
      await fetchAndSetCoupons()
    } catch (error) {
      console.error('Error pausing coupon:', error)
      //     const userStore = useUserStore();
      //     userStore.$reset();
      //     localStorage.clear();
      //     userStore.clearUser()
      // window.location.href = "/";
    }
  }

  async function getRecurringCoupon(parentCouponId: string) {
    try {
      const userStore = useUserStore()
      const funcUser = () => {
        if (
          userStore?.userType == 'company_admin' ||
          userStore?.userType == 'company_suadmin' ||
          userStore?.userType == 'company_owner' ||
          userStore?.userType == 'company_user'
        ) {
          return 'admin'
        } else if (
          userStore?.userType == 'client_admin' ||
          userStore?.userType == 'client_manager' ||
          userStore?.userType == 'client_user'
        ) {
          return 'user'
        }
      }
      const response = await axios.post(
        `${getEndpointURL(
          userStore?.userType || '',
          userStore?.productSetting?.couponId || '',
        )}/secdual/recurring`,
        {
          parentCouponId: parentCouponId,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userStore?.cognitoToken}`,
          },
        },
      )

      const fetchedCoupon = response.data
      console.log('response for child coupon', fetchedCoupon)

      return fetchedCoupon
    } catch (error) {
      console.error('Error fetching coupon details:', error)
      //     const userStore = useUserStore();
      //     userStore.$reset();
      //     localStorage.clear();
      //     userStore.clearUser()
      // window.location.href = "/";
      //     return null;
    }
  }

  function toLocalDateTimeInput(utcIso: string): string {
    const d = new Date(utcIso)
    const pad = (n: number) => n.toString().padStart(2, '0')
    return (
      [d.getFullYear(), pad(d.getMonth() + 1), pad(d.getDate())].join('-') +
      'T' +
      [pad(d.getHours()), pad(d.getMinutes())].join(':')
    )
  }

  return {
    count,
    doubleCount,
    coupons,
    increment,
    fetchAndSetCoupons,
    fetchChildCoupon,
    createCoupon,
    getCouponDetails,
    deleteCoupon,
    pauseCoupon,
    updateCoupon,
    getRecurringCoupon,
  }
})
